/*
 * "Load more" for the collection product grid.
 * Every click shows exactly `perPage` products. Available products come first, in collection order,
 * fetched across as many server pages as needed. Sold out products ([data-sold-out]) from every page
 * are held back and only released, `perPage` at a time, once all server pages have been consumed.
 */
if (!customElements.get('collection-load-more')) {
  const isProduct = (el) => el.hasAttribute('data-grid-product');

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.grid = document.getElementById(this.dataset.productGrid);
      this.button = this.querySelector('button');
      if (!this.grid || !this.button) return;

      // Deep links to ?page=N would skip the held back sold out products of earlier pages.
      if (parseInt(this.dataset.currentPage, 10) > 1 && !window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        if (url.searchParams.has('page')) {
          url.searchParams.delete('page');
          window.location.replace(url.toString());
          return;
        }
      }

      this.perPage = parseInt(this.dataset.perPage, 10) || 24;
      this.nextUrl = this.dataset.nextUrl || null;
      this.pending = [];
      this.soldOut = [];
      this.seen = new Set();
      this.loading = false;
      this.abortController = new AbortController();

      Array.from(this.grid.children).forEach((el) => {
        if (isProduct(el)) this.seen.add(el.dataset.gridProduct);
        if (el.hasAttribute('data-sold-out')) {
          el.remove();
          this.soldOut.push(el);
        }
      });

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      const shown = Array.from(this.grid.children).filter(isProduct).length;
      if (shown < this.perPage && this.hasMore()) {
        this.load(this.perPage - shown, false);
      } else {
        this.updateVisibility();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.button?.removeEventListener('click', this.onClick);
    }

    onClick() {
      this.load(this.perPage);
    }

    hasMore() {
      return Boolean(this.nextUrl) || this.soldOut.length > 0 || this.pending.some(isProduct);
    }

    async load(count, showOverlay = true) {
      if (this.loading) return;
      this.loading = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      const overlayTarget = this.grid.parentElement;
      if (showOverlay) overlayTarget.classList.add('loading');

      try {
        const items = await this.collect(count);
        if (!this.isConnected || items.length === 0) return;

        const fragment = document.createDocumentFragment();
        items.forEach((el) => {
          el.removeAttribute('hidden');
          fragment.appendChild(el);
        });
        this.grid.appendChild(fragment);

        const animateList = this.closest('appear-animate-list');
        if (window.appearAnimate && animateList) animateList.reset();
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.loading = false;
        overlayTarget.classList.remove('loading');
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        this.updateVisibility();
      }
    }

    async collect(count) {
      const taken = [];
      let products = 0;

      try {
        while (products < count) {
          if (this.pending.length) {
            const el = this.pending.shift();
            taken.push(el);
            if (isProduct(el)) products++;
          } else if (this.nextUrl) {
            await this.fetchNextPage();
          } else if (this.soldOut.length) {
            taken.push(this.soldOut.shift());
            products++;
          } else {
            break;
          }
        }
      } catch (error) {
        // Fetching only happens before any sold out item is taken, so everything taken came from `pending`.
        this.pending.unshift(...taken);
        throw error;
      }

      // Don't strand trailing promotion tiles once there are no products left to show.
      if (!this.hasMore()) taken.push(...this.pending.splice(0));

      return taken;
    }

    async fetchNextPage() {
      const response = await fetch(this.nextUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Load more failed: HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = html.getElementById(this.dataset.productGrid);
      const loader = html.querySelector('collection-load-more');

      this.nextUrl = loader?.dataset.nextUrl || null;
      if (!grid) return;

      Array.from(grid.children).forEach((el) => {
        if (isProduct(el)) {
          // Product order can shift between requests (e.g. stock changes); never show a product twice.
          if (this.seen.has(el.dataset.gridProduct)) return;
          this.seen.add(el.dataset.gridProduct);
        }
        (el.hasAttribute('data-sold-out') ? this.soldOut : this.pending).push(el);
      });
    }

    updateVisibility() {
      this.hidden = !this.hasMore();
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
